import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ForEach,
  H5,
  Stack,
  Tabs,
} from '@fashionnovateam/nova-ui-toolkit';
import {
  PurchaseOrder,
  PurchaseOrderActivity,
  PurchaseOrderActivityType,
} from '../../../Lib/Types/purchase-order';
import { Vendor } from '../../../Lib/Api/vendor';
import {
  ChatMessage,
  PurchaseOrderChatMessage,
  useChatContext,
  useChatMessages,
  useConversationById,
  useLastMessage,
} from '../../Chat';
import { renderActivity } from './renderActivity';
import { ConversationViewMessage } from '../../Counters/drawer/Chat';
import { MessageInput } from './MessageInput';
import { JoinChat } from './JoinChat';
import { PurchaseOrderRevisionStatus } from '../../../Lib/Types/purchase-order-revision';
import { useGetUserPurchaseOrderChat } from '../../../Lib/Hooks/purchase-orders.hooks';
import { ChatBackMessage } from './ChatBackMessage';

export type ModernActivitiesProps = {
  purchaseOrder: PurchaseOrder;
  userId: string;
  vendor: Vendor;
};

type FeedMessage = {
  type: 'message';
  message: ChatMessage;
};

type FeedBackMessage = {
  type: 'back-message';
  message: PurchaseOrderChatMessage;
};

type FeedActivity = {
  type: 'activity';
  activity: PurchaseOrderActivity;
};

export type FeedItem = FeedMessage | FeedBackMessage | FeedActivity;

export function ModernActivities(props: ModernActivitiesProps) {
  const { purchaseOrder, userId } = props;

  const [tab, setTab] = useState<'all' | 'message' | 'activity'>('all');

  const { client, status } = useChatContext();
  const isConnected = client && status === 'connected';

  const conversationProcess = useConversationById(
    userId,
    isConnected ? client : undefined,
    'po',
    purchaseOrder.po_number,
  );

  const conversation = !conversationProcess.isError
    ? conversationProcess.data
    : undefined;
  const notInChat =
    conversationProcess.error instanceof Error &&
    (conversationProcess.error.message === 'Forbidden' ||
      conversationProcess.error.message.startsWith('Access forbidden'));

  const isChatInputEnabled =
    PurchaseOrderRevisionStatus.REVISION_PD_IN_NEGOTIATION ===
      purchaseOrder.vendor_revision?.status ||
    PurchaseOrderRevisionStatus.REVISION_NEGOTIATION ===
      purchaseOrder.vendor_revision?.status;

  const messagesProcess = useChatMessages(userId, conversation?.instance);

  const backendMessagesProcess = useGetUserPurchaseOrderChat(
    userId,
    purchaseOrder.po_number,
  );

  const feed: FeedItem[] = useMemo(() => {
    const messages = tab !== 'activity' ? messagesProcess.messages : [];
    const backMessages =
      tab !== 'activity' && !messages?.length
        ? backendMessagesProcess.data || []
        : [];
    const activities =
      tab !== 'message' ? purchaseOrder.purchase_order_activities || [] : [];

    const combined: FeedItem[] = [
      ...messages.map<FeedMessage>((m) => ({
        type: 'message',
        message: m,
      })),
      ...backMessages.map<FeedBackMessage>((m) => ({
        type: 'back-message',
        message: m,
      })),
      ...activities
        .map<FeedActivity>((a) => ({
          type: 'activity',
          activity: a,
        }))
        .filter(
          (item) =>
            !(
              item.activity.type ===
                PurchaseOrderActivityType.REVISION_STATUS_UPDATED &&
              item.activity.details?.hidden
            ),
        ),
    ].sort((a, b) => {
      const dateA =
        // eslint-disable-next-line no-nested-ternary
        a.type === 'message'
          ? a.message.instance.dateCreated || new Date()
          : a.type === 'back-message'
            ? new Date(a.message.dateCreated)
            : new Date(a.activity.created_at);
      const dateB =
        // eslint-disable-next-line no-nested-ternary
        b.type === 'message'
          ? b.message.instance.dateCreated || new Date()
          : b.type === 'back-message'
            ? new Date(b.message.dateCreated)
            : new Date(b.activity.created_at);
      return dateA.getTime() - dateB.getTime();
    });

    return combined;
  }, [
    messagesProcess.messages,
    backendMessagesProcess.data,
    purchaseOrder.purchase_order_activities,
    tab,
  ]);

  const lastMessageProcess = useLastMessage(userId, conversation?.instance);
  useEffect(() => {
    if (
      conversation &&
      lastMessageProcess.data?.instance.index &&
      (!conversation.instance.lastReadMessageIndex ||
        conversation.instance.lastReadMessageIndex <
          lastMessageProcess.data.instance.index)
    ) {
      conversation.instance.updateLastReadMessageIndex(
        lastMessageProcess.data.instance.index,
      );
    }
  }, [
    lastMessageProcess.data?.instance.index,
    conversation?.instance.lastReadMessageIndex,
  ]);

  return (
    <Stack direction="col" className="gap-6">
      <H5>Activity</H5>
      <Tabs.Group type="segment">
        <Tabs.Item
          type="segment"
          isActive={tab === 'all'}
          onClick={() => setTab('all')}
        >
          All
        </Tabs.Item>
        <Tabs.Item
          type="segment"
          isActive={tab === 'message'}
          onClick={() => setTab('message')}
        >
          Messages
        </Tabs.Item>
        <Tabs.Item
          type="segment"
          isActive={tab === 'activity'}
          onClick={() => setTab('activity')}
          title="Activity"
        >
          Activity
        </Tabs.Item>
      </Tabs.Group>
      <Stack direction="col" className="gap-3" width="none">
        {messagesProcess.hasPreviousPage && (
          <div key="load-messages" className="w-full flex justify-center p-4">
            <Button
              loading={messagesProcess.isFetchingPreviousPage}
              onClick={() => messagesProcess.fetchPreviousPage()}
            >
              Load more messages
            </Button>
          </div>
        )}
        <ForEach items={feed}>
          {(event, index) => {
            if (event.type === 'activity') {
              return renderActivity(
                event.activity,
                purchaseOrder,
                index === feed.length - 1,
              );
            }
            if (event.type === 'back-message') {
              return (
                <ChatBackMessage
                  key={event.message.sid}
                  message={event.message}
                  isLast={index === feed.length - 1}
                />
              );
            }
            return (
              <ConversationViewMessage
                key={event.message.sid}
                message={event.message}
                isLast={index === feed.length - 1}
              />
            );
          }}
        </ForEach>
      </Stack>
      {isChatInputEnabled && tab !== 'activity' && (
        <div className="flex justify-center max-w-[620px]">
          {conversation && <MessageInput conversation={conversation} />}
          {notInChat && (
            <JoinChat
              userId={userId}
              purchaseOrderId={purchaseOrder.po_number}
            />
          )}
        </div>
      )}
    </Stack>
  );
}
